"use client";

import { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Stack,
  Fade,
  ButtonGroup,
} from "@mui/material";
import {
  Code,
  ContentCopy,
  Delete,
  SwapHoriz,
  CheckCircle,
} from "@mui/icons-material";

type Mode = "encode" | "decode";

export default function EncodeDecodeUriComponentConverter() {
  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleTransform = () => {
    setError(null);
    const text = input.trim();

    if (!text) {
      setError("Please enter some text to transform.");
      return;
    }

    try {
      if (mode === "encode") {
        setOutput(encodeURIComponent(text));
      } else {
        setOutput(decodeURIComponent(text));
      }
    } catch {
      setError(
        mode === "decode"
          ? "Invalid URI component. The string may contain malformed escape sequences."
          : "Failed to encode. Please check your input."
      );
      setOutput("");
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setError(null);
    setCopied(false);
  };

  const handleSwap = () => {
    if (output) {
      setInput(output);
      setOutput("");
      setMode((m) => (m === "encode" ? "decode" : "encode"));
      setError(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Mode Toggle */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          <ButtonGroup variant="outlined">
            <Button
              variant={mode === "encode" ? "contained" : "outlined"}
              onClick={() => {
                setMode("encode");
                setError(null);
                setOutput("");
              }}
              sx={{
                ...(mode === "encode" && {
                  background:
                    "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                }),
              }}
            >
              Encode
            </Button>
            <Button
              variant={mode === "decode" ? "contained" : "outlined"}
              onClick={() => {
                setMode("decode");
                setError(null);
                setOutput("");
              }}
              sx={{
                ...(mode === "decode" && {
                  background:
                    "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                }),
              }}
            >
              Decode
            </Button>
          </ButtonGroup>
        </Stack>

        {/* Input */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {mode === "encode" ? "Text to encode" : "URI component to decode"}
        </Typography>
        <TextField
          multiline
          rows={6}
          fullWidth
          placeholder={
            mode === "encode"
              ? "Enter or paste text to encode for use in URI (e.g. query strings)..."
              : "Enter or paste an encoded URI component to decode..."
          }
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (output) setOutput("");
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            onClick={handleTransform}
            disabled={!input.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {mode === "encode" ? "Encode for URL" : "Decode from URL"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>

        {/* Output */}
        {output && (
          <Fade in={!!output}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {mode === "encode" ? "Encoded output" : "Decoded text"}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={handleCopy}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<SwapHoriz />}
                    onClick={handleSwap}
                    variant="outlined"
                  >
                    Swap
                  </Button>
                </Stack>
              </Box>

              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    color: "#d4d4d4",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {output}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
